import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  ShieldCheck, 
  AlertTriangle, 
  Check, 
  X, 
  Loader2,
  Eye,
  Clock,
  User,
  Mail,
  Phone,
  FileText,
  Image,
  ChevronRight,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const STATUS_COLORS = {
  pending_check: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-green-100 text-green-700 border-green-200",
  conflict: "bg-red-100 text-red-700 border-red-200"
};

export const ConflictCheck = () => {
  const [trademarks, setTrademarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTrademark, setSelectedTrademark] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictNotes, setConflictNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchPendingTrademarks();
  }, []);

  const fetchPendingTrademarks = async () => {
    try {
      const response = await axios.get(`${API}/conflict-check/pending`);
      setTrademarks(response.data);
    } catch (error) {
      console.error('Error fetching pending trademarks:', error);
      toast.error('Failed to load pending trademarks');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (trademark) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/conflict-check/${trademark.id}/approve`);
      toast.success(`"${trademark.name}" has been approved and moved to My Trade Marks`);
      fetchPendingTrademarks();
      setShowDetailModal(false);
    } catch (error) {
      console.error('Error approving trademark:', error);
      toast.error('Failed to approve trademark');
    } finally {
      setProcessing(false);
    }
  };

  const handleConflict = async () => {
    if (!selectedTrademark) return;
    
    setProcessing(true);
    try {
      await axios.post(`${API}/conflict-check/${selectedTrademark.id}/conflict`, {
        notes: conflictNotes
      });
      toast.success(`Conflict flagged for "${selectedTrademark.name}". Team will follow up.`);
      fetchPendingTrademarks();
      setShowConflictModal(false);
      setShowDetailModal(false);
      setConflictNotes("");
    } catch (error) {
      console.error('Error flagging conflict:', error);
      toast.error('Failed to flag conflict');
    } finally {
      setProcessing(false);
    }
  };

  const openConflictModal = (trademark) => {
    setSelectedTrademark(trademark);
    setShowConflictModal(true);
  };

  const filteredTrademarks = trademarks.filter(tm =>
    tm.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tm.owner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tm.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const handleExportCSV = () => {
    if (trademarks.length === 0) {
      toast.error('No records to export');
      return;
    }
    const headers = "Trade Mark Name,Owner,Owner Address,Contact Email,Contact Phone,Registration Number,Registration Date,Status,Conflict Status,Nice Classes,Description,Submitted Date\n";
    const rows = trademarks.map(tm => {
      const classes = (tm.categories || []).map(c => `Class ${c}`).join('; ');
      const regDate = tm.registration_date ? new Date(tm.registration_date).toLocaleDateString('en-GB') : '';
      const subDate = tm.submitted_at ? new Date(tm.submitted_at).toLocaleDateString('en-GB') : '';
      return [
        `"${(tm.name || '').replace(/"/g, '""')}"`,
        `"${(tm.owner || '').replace(/"/g, '""')}"`,
        `"${(tm.owner_address || '').replace(/"/g, '""').replace(/\n/g, ', ')}"`,
        `"${tm.contact_email || ''}"`,
        `"${tm.contact_phone || ''}"`,
        `"${tm.registration_number || ''}"`,
        `"${regDate}"`,
        `"${(tm.trademark_status || '').replace('_', ' ')}"`,
        `"${(tm.conflict_status || '').replace('_', ' ')}"`,
        `"${classes}"`,
        `"${(tm.description || '').replace(/"/g, '""')}"`,
        `"${subDate}"`
      ].join(',');
    }).join('\n');

    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conflict_check_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="conflict-check-title">
            Conflict Check
          </h1>
          <p className="text-slate-500 mt-1">
            Review new trade mark submissions before adding to monitoring
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-lg border border-amber-200">
            <Clock className="w-5 h-5" />
            <span className="font-medium">{trademarks.filter(t => t.conflict_status === 'pending_check').length} Pending Review</span>
          </div>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            className="gap-2"
            data-testid="export-conflict-csv-btn"
          >
            <FileText className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search by name, owner or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="conflict-search-input"
        />
      </div>

      {/* List */}
      {filteredTrademarks.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl border border-slate-200">
          <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No Pending Trade Marks</h3>
          <p className="text-slate-400 mt-1">All submissions have been reviewed</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTrademarks.map((trademark, index) => (
            <motion.div
              key={trademark.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow"
              data-testid={`conflict-item-${trademark.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  {trademark.logo_url ? (
                    <img 
                      src={trademark.logo_url} 
                      alt={trademark.name}
                      className="w-16 h-16 rounded-lg object-contain bg-slate-50 border border-slate-200"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-slate-400" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">{trademark.name}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {trademark.owner}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {trademark.contact_email}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {trademark.categories?.slice(0, 5).map((cat) => (
                        <span key={cat} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                          Class {cat}
                        </span>
                      ))}
                      {trademark.categories?.length > 5 && (
                        <span className="text-xs text-slate-400">
                          +{trademark.categories.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[trademark.conflict_status] || STATUS_COLORS.pending_check}`}>
                    {trademark.conflict_status === 'pending_check' && 'Pending Review'}
                    {trademark.conflict_status === 'approved' && 'Approved'}
                    {trademark.conflict_status === 'conflict' && 'Conflict Identified'}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedTrademark(trademark);
                      setShowDetailModal(true);
                    }}
                    data-testid={`view-details-${trademark.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </Button>

                  {trademark.conflict_status === 'pending_check' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleApprove(trademark)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-${trademark.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openConflictModal(trademark)}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`conflict-${trademark.id}`}
                      >
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        Conflict
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {trademark.submitted_at && (
                <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400">
                  Submitted: {new Date(trademark.submitted_at).toLocaleString()}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedTrademark?.logo_url && (
                <img 
                  src={selectedTrademark.logo_url} 
                  alt="" 
                  className="w-10 h-10 rounded object-contain bg-slate-50"
                />
              )}
              {selectedTrademark?.name}
            </DialogTitle>
            <DialogDescription>
              Trade mark details submitted for monitoring
            </DialogDescription>
          </DialogHeader>

          {selectedTrademark && (
            <div className="space-y-4 py-4">
              {/* Logo display */}
              {selectedTrademark.logo_url && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Logo</label>
                  <div className="mt-1 p-4 bg-slate-50 rounded-lg border border-slate-200 inline-block">
                    <img 
                      src={selectedTrademark.logo_url} 
                      alt={selectedTrademark.name}
                      className="max-w-[200px] max-h-[200px] object-contain"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500">Trade Mark Name</label>
                  <p className="text-slate-900 font-medium">{selectedTrademark.name}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Trade Mark Status</label>
                  <p className="text-slate-900 capitalize">{selectedTrademark.trademark_status?.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Owner / Company Name</label>
                  <p className="text-slate-900">{selectedTrademark.owner}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Registration Number</label>
                  <p className="text-slate-900">{selectedTrademark.registration_number || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Contact Email</label>
                  <p className="text-slate-900">{selectedTrademark.contact_email}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Contact Phone</label>
                  <p className="text-slate-900">{selectedTrademark.contact_phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Registration Date</label>
                  <p className="text-slate-900">
                    {selectedTrademark.registration_date 
                      ? new Date(selectedTrademark.registration_date).toLocaleDateString('en-GB') 
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Submitted</label>
                  <p className="text-slate-900">
                    {selectedTrademark.submitted_at 
                      ? new Date(selectedTrademark.submitted_at).toLocaleDateString('en-GB') 
                      : '-'}
                  </p>
                </div>
              </div>

              {/* Owner Address - full width */}
              {selectedTrademark.owner_address && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Owner Address</label>
                  <p className="text-slate-900 mt-1 whitespace-pre-line">{selectedTrademark.owner_address}</p>
                </div>
              )}

              {selectedTrademark.description && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Description</label>
                  <p className="text-slate-900 mt-1">{selectedTrademark.description}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-500">Nice Classes</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedTrademark.categories?.length > 0 ? selectedTrademark.categories.map((cat) => (
                    <span key={cat} className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded">
                      Class {cat}
                    </span>
                  )) : <span className="text-sm text-slate-400">None specified</span>}
                </div>
              </div>

              {selectedTrademark.conflict_notes && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <label className="text-xs font-medium text-red-600">Conflict Notes</label>
                  <p className="text-red-700 mt-1">{selectedTrademark.conflict_notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedTrademark?.conflict_status === 'pending_check' && (
              <>
                <Button
                  variant="outline"
                  onClick={() => openConflictModal(selectedTrademark)}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Flag Conflict
                </Button>
                <Button
                  onClick={() => handleApprove(selectedTrademark)}
                  disabled={processing}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  Approve & Add to Monitoring
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conflict Modal */}
      <Dialog open={showConflictModal} onOpenChange={setShowConflictModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Flag Conflict for "{selectedTrademark?.name}"
            </DialogTitle>
            <DialogDescription>
              This will mark the trade mark as having a conflict. A team member will need to contact the client.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="text-sm font-medium text-slate-700">
              Conflict Notes <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={conflictNotes}
              onChange={(e) => setConflictNotes(e.target.value)}
              placeholder="Describe the conflict identified (e.g., existing similar mark, class overlap, etc.)"
              rows={4}
              className="mt-2"
              data-testid="conflict-notes-input"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConflictModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConflict}
              disabled={processing || !conflictNotes.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-conflict-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <AlertTriangle className="w-4 h-4 mr-1" />}
              Confirm Conflict
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConflictCheck;
